import { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Lock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchApps,
  requestAppSwitch,
  type OnxiteApp,
} from '@/lib/api/apps';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/Toast';

/**
 * La rejilla 3×3 — el paso de una aplicación a otra.
 *
 * Hace dos cosas que conviene no confundir, porque sirven a gente distinta:
 *
 * 1. **A quien tiene varias aplicaciones**, le ahorra volver a identificarse.
 *    El backend ya sabe que puede entrar; pedirle la contraseña otra vez no
 *    protege nada.
 * 2. **A quien tiene una sola** —la inmensa mayoría— le enseña que existen las
 *    demás. Es la única superficie del producto donde el ecosistema se ve como
 *    un ecosistema, así que el candado merece tanto cuidado como el salto.
 *
 * La lista la decide el backend (`GET /auth/apps`). Aquí no se filtra nada: si
 * el criterio de quién ve qué viviera también en el cliente, los dos se irían
 * separando y el candado acabaría mintiendo.
 */
export function AppSwitcher() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [saltando, setSaltando] = useState<string | null>(null);
  /**
   * Se acercó al botón: se va pidiendo lo que va a hacer falta.
   *
   * Pedirlo al ABRIR se veía: primero el catálogo y después sus seis iconos,
   * apareciendo de uno en uno con el menú ya delante. Al pasar el ratón —o al
   * llegar con el tabulador— hay unos cientos de milisegundos que se pueden
   * aprovechar, y quien no se acerca no gasta ninguna petición.
   */
  const [cerca, setCerca] = useState(false);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: queryKeys.auth.apps,
    queryFn: fetchApps,
    // El catálogo cambia cuando se contrata algo, o sea casi nunca. Pedirlo en
    // cada apertura del menú sería una llamada por curiosidad.
    staleTime: 5 * 60 * 1000,
    // Cuando el menú se abre o cuando se ve venir. Quien nunca se acerca al
    // botón no pide nada, que es lo que este `enabled` protegía.
    enabled: open || cerca,
  });

  /*
   * Los iconos, en cuanto se sabe cuáles son.
   *
   * El navegador los guarda en su caché, así que al abrir el menú ya están
   * pintados en vez de llegando. Son seis PNG de 10 kB: cargarlos antes cuesta
   * menos que verlos aparecer.
   */
  useEffect(() => {
    for (const app of apps) {
      if (!app.iconUrl) continue;
      const img = new Image();
      img.src = app.iconUrl;
    }
  }, [apps]);

  /**
   * Saltar a otra aplicación: **pestaña nueva y con el foco puesto**.
   *
   * Quien cambia de aplicación no está abandonando ésta: vuelve. Sustituir la
   * pestaña obliga a rehacer el camino al volver —y a perder lo que estuviera a
   * medias en un panel—, así que se abre al lado.
   *
   * **Y si esa aplicación ya está abierta, se va a ella**: el nombre de
   * ventana (`onxite-app-xurvey`) hace que el navegador reutilice la pestaña
   * en vez de abrir la séptima. Cuando ya existe sólo se enfoca, sin recargar
   * — recargarla borraría el trabajo de quien está allí, que es justo lo que
   * esto viene a evitar.
   *
   * Lo que este mecanismo NO puede: reconocer una pestaña que el usuario abrió
   * a mano escribiendo la URL. Ésa no tiene nombre y no hay forma de
   * enumerarla desde una página.
   *
   * **La pestaña se abre ANTES de pedir el código, dentro del clic.** Es lo
   * único que no se puede reordenar: un `window.open` después de un `await` ya
   * no cuenta como gesto del usuario y el navegador lo bloquea sin avisar a
   * nadie más que a la barra de direcciones. Se abre en blanco, se le pone un
   * cartel, y cuando llega el código se la manda a su destino.
   */
  async function saltar(app: OnxiteApp) {
    if (!app.url || saltando) return;
    setSaltando(app.code);

    const tab = window.open('', ventanaDeApp(app.code));

    if (tab && yaEstabaAbierta(tab)) {
      // Ya está corriendo ahí: sólo se trae al frente.
      tab.focus();
      setSaltando(null);
      setOpen(false);
      return;
    }

    if (tab) {
      /*
       * Un cartel mientras tanto.
       *
       * Pedir el código tarda poco, pero con mala red esa pestaña en blanco es
       * lo único que ve quien acaba de pulsar, y una pestaña en blanco se lee
       * como algo que se ha roto.
       */
      tab.document.write(
        `<!doctype html><meta charset="utf-8"><title>${app.name}</title>` +
          '<body style="margin:0;background:#0b0f14;color:#e8eef5;font:16px system-ui,sans-serif;' +
          'display:grid;place-items:center;height:100vh">' +
          `${t('apps.opening', { app: app.name })}</body>`,
      );
      tab.document.close();
    }

    try {
      const { code, url } = await requestAppSwitch(app.code);
      const destino = `${url}?code=${encodeURIComponent(code)}`;
      if (tab) {
        // `replace` y no `href`: así el cartel en blanco no queda en el
        // historial de la pestaña nueva y su «atrás» no lleva a ninguna parte.
        tab.location.replace(destino);
        // Chrome ya la enfoca al abrirla desde un clic; esto lo asegura en los
        // que no, que es literalmente lo que se pidió.
        tab.focus();
      } else {
        // El navegador bloqueó la pestaña. Antes que dejar el clic sin efecto,
        // se salta en ésta: es el comportamiento de siempre.
        window.location.replace(destino);
      }
      setSaltando(null);
      // El menú se cierra: lo que se iba a hacer ya está pasando en otro sitio.
      setOpen(false);
    } catch {
      // Sin código no hay destino, y una pestaña en blanco abierta para nada es
      // basura que el usuario tiene que cerrar a mano.
      tab?.close();
      setSaltando(null);
      toast({ tone: 'error', title: t('apps.switchFailed') });
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('apps.open')}
          title={t('apps.open')}
          // Con el ratón encima y con el foco: en un teclado no hay «encima»,
          // y quien navega con tabulador merece el mismo menú instantáneo.
          onMouseEnter={() => setCerca(true)}
          onFocus={() => setCerca(true)}
          className="grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-accent-ink)]"
        >
          <RejillaIcon className="h-5 w-5" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          // Anclado al borde derecho porque el disparador vive en la esquina
          // derecha de la barra: con `start` el panel de 300 px crecería hacia
          // fuera de la ventana y Radix tendría que recolocarlo de un salto.
          align="end"
          sideOffset={8}
          className="z-50 w-[300px] rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-2 shadow-xl"
        >
          <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-xk-lavender)]">
            {t('apps.title')}
          </p>

          {isLoading ? (
            <p className="px-2 py-6 text-center text-xs text-[var(--color-xk-lavender)]">
              {t('common.loading')}
            </p>
          ) : (
            <ul className="grid grid-cols-3 gap-1">
              {apps.map((app) => (
                <li key={app.code}>
                  <AppTile
                    app={app}
                    saltando={saltando === app.code}
                    onSaltar={() => void saltar(app)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AppTile({
  app,
  saltando,
  onSaltar,
}: {
  app: OnxiteApp;
  saltando: boolean;
  onSaltar: () => void;
}) {
  const t = useT();
  const esActual = app.state === 'current';
  const bloqueada = app.state === 'locked';
  const proxima = app.state === 'coming-soon';

  const contenido = (
    <>
      <span className="relative">
        <AppIcon app={app} />
        {bloqueada && (
          <span
            className="absolute -bottom-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full bg-[var(--color-xk-bg-deep)]"
            aria-hidden
          >
            <Lock className="h-2.5 w-2.5 text-[var(--color-xk-lavender)]" />
          </span>
        )}
      </span>
      <span className="mt-1.5 w-full truncate text-center text-[11px] leading-tight">
        {app.name}
      </span>
      {proxima && (
        <span className="text-[9px] leading-tight text-[var(--color-xk-lavender)]">
          {t('apps.comingSoon')}
        </span>
      )}
    </>
  );

  const clases = cn(
    'flex w-full flex-col items-center rounded-md px-1 py-2.5 transition-colors',
    esActual && 'bg-[var(--color-xk-surface)] font-semibold',
    proxima && 'cursor-default opacity-45',
    !proxima && !esActual && 'hover:bg-[var(--color-xk-surface)]',
    saltando && 'opacity-60',
  );

  // «Próximamente» no es un enlace ni un botón: no hay nada que pulsar. Dejarlo
  // como botón inerte lo pondría en el recorrido del tabulador prometiendo algo
  // que no ocurre al pulsar Intro.
  if (proxima) {
    return (
      <div className={clases} aria-disabled>
        {contenido}
      </div>
    );
  }

  if (esActual) {
    return (
      <div className={clases} aria-current="page">
        {contenido}
        <span className="sr-only">{t('apps.current')}</span>
      </div>
    );
  }

  /*
   * Con candado: NO se va a ningún sitio.
   *
   * Era un enlace a la página de contacto, y el razonamiento tenía sentido
   * —quien decide la compra no siempre es quien mira la pantalla— pero en la
   * práctica hacía lo contrario de lo que promete la rejilla: pulsabas
   * «Xurvey» esperando ir a Xurvey y acababas en `onxite.com`, fuera del
   * producto, sin haber pedido salir. Reportado como si fuera una URL mal
   * configurada; no lo era.
   *
   * El candado ya dice todo lo que hay que decir. Se queda como una casilla
   * inerte —misma pinta, sin enlace, `aria-disabled`— y el motivo va en el
   * `title` y para el lector de pantalla.
   */
  if (bloqueada) {
    return (
      <div className={clases} aria-disabled title={t('apps.lockedHint')}>
        {contenido}
        <span className="sr-only">{t('apps.lockedHint')}</span>
      </div>
    );
  }

  return (
    <button type="button" onClick={onSaltar} disabled={saltando} className={clases}>
      {contenido}
    </button>
  );
}

/**
 * El icono de la aplicación.
 *
 * Mientras no llegue el arte definitivo se pinta la inicial sobre el color de
 * la marca. Los iconos se sirven desde el backend, así que cuando lleguen es
 * dejarlos caer en `uploads/apps/` — sin desplegar ningún frontend.
 */
function AppIcon({ app }: { app: OnxiteApp }) {
  const [cargada, setCargada] = useState(false);

  if (app.iconUrl) {
    return (
      <span
        aria-hidden
        className="grid h-9 w-9 place-items-center rounded-lg"
        /*
         * El hueco, con el color de la marca al 18 % hasta que llegue la
         * imagen.
         *
         * No es decoración: sin fondo, el icono que tarda deja un agujero del
         * color del menú y la rejilla se lee como si le faltaran piezas. Con el
         * hueco puesto, lo que ocurre es que una casilla se colorea — que es lo
         * que pasa de verdad.
         */
        style={cargada ? undefined : { backgroundColor: `${app.brandColor}2e` }}
      >
        <img
          src={app.iconUrl}
          alt=""
          aria-hidden
          onLoad={() => setCargada(true)}
          // `eager` y no `lazy`: son seis iconos de 10 kB dentro de un menú que
          // ya está abierto; diferirlos es justo el efecto que se quiere quitar.
          loading="eager"
          decoding="async"
          className="h-9 w-9 rounded-lg object-contain transition-opacity duration-150"
          style={{ opacity: cargada ? 1 : 0 }}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="grid h-9 w-9 place-items-center rounded-lg text-sm font-bold text-white"
      style={{ backgroundColor: app.brandColor }}
    >
      {app.name.charAt(0)}
    </span>
  );
}

/**
 * La rejilla de nueve cuadraditos.
 *
 * Va a mano porque lucide-react no la trae: `LayoutGrid` son cuatro cuadrados
 * y `Grip` seis puntos. Ninguno se lee como «cambiar de aplicación», que es un
 * gesto que la gente ya tiene aprendido con esta forma exacta.
 */
function RejillaIcon({ className }: { className?: string }) {
  const pos = [3, 10.5, 18];
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      {pos.map((y) =>
        pos.map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="3" height="3" rx="1" />
        )),
      )}
    </svg>
  );
}

/** El nombre de ventana de cada aplicación. De él sale la reutilización. */
const ventanaDeApp = (code: string) => `onxite-app-${code}`;

/**
 * ¿Esa pestaña ya estaba abierta con la aplicación dentro?
 *
 * Una pestaña recién creada está en `about:blank` y se puede leer. Una que ya
 * corre otra aplicación es de **otro origen**, así que leer su `location`
 * lanza — y ese error es precisamente la respuesta: existe y no es nuestra.
 */
function yaEstabaAbierta(tab: Window): boolean {
  try {
    return tab.location.href !== 'about:blank' && tab.location.href !== '';
  } catch {
    return true;
  }
}
